import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { AuthService } from '../../../services/auth.service';

export type AuthView = 'login' | 'register' | 'forgot' | 'confirmRegister' | 'confirmForgot';

@Component({
  selector: 'app-auth-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, ButtonModule, DialogModule],
  templateUrl: './auth-dialog.component.html',
  styleUrl: './auth-dialog.component.scss',
})
export class AuthDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);

  visible = input<boolean>(false);
  visibleChange = output<boolean>();
  viewChange = output<AuthView>();
  success = output<void>();

  view = input.required<AuthView>();

  protected showLoginPassword = signal(false);
  protected showRegisterPassword = signal(false);
  protected showRegisterConfirmPassword = signal(false);
  protected showConfirmForgotPassword = signal(false);
  protected showConfirmForgotConfirm = signal(false);
  protected submitting = signal(false);

  protected headerText = computed(() => {
    const v = this.view();
    const map: Record<AuthView, string> = {
      login: 'Sign in',
      register: 'Create account',
      forgot: 'Reset password',
      confirmRegister: 'Verify your email',
      confirmForgot: 'Set new password',
    };
    return map[v] ?? 'Sign in';
  });

  protected loginForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  protected registerForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', Validators.required],
  });

  protected forgotForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  protected confirmRegisterForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.minLength(6)]],
  });

  protected confirmForgotForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.minLength(6)]],
    newPassword: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', Validators.required],
  });

  protected pendingEmail = signal<string>('');
  protected errorMessage = this.auth.error;

  protected onHide(): void {
    this.auth.clearError();
    this.visibleChange.emit(false);
  }

  protected async onLoginSubmit(): Promise<void> {
    if (!this.loginForm.valid) return;
    this.submitting.set(true);
    this.auth.clearError();
    try {
      const { email, password } = this.loginForm.getRawValue();
      const result = await this.auth.signIn(email, password);
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        this.pendingEmail.set(email);
        this.viewChange.emit('confirmRegister');
      } else {
        this.success.emit();
        this.visibleChange.emit(false);
      }
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async onRegisterSubmit(): Promise<void> {
    if (!this.registerForm.valid) return;
    const { email, password, confirmPassword } = this.registerForm.getRawValue();
    if (password !== confirmPassword) {
      this.registerForm.controls.confirmPassword.setErrors({ mismatch: true });
      return;
    }
    this.submitting.set(true);
    this.auth.clearError();
    try {
      await this.auth.signUp(email, password);
      this.pendingEmail.set(email);
      this.viewChange.emit('confirmRegister');
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async onForgotSubmit(): Promise<void> {
    if (!this.forgotForm.valid) return;
    this.submitting.set(true);
    this.auth.clearError();
    try {
      const { email } = this.forgotForm.getRawValue();
      await this.auth.forgotPassword(email);
      this.pendingEmail.set(email);
      this.viewChange.emit('confirmForgot');
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async onConfirmRegisterSubmit(): Promise<void> {
    if (!this.confirmRegisterForm.valid) return;
    this.submitting.set(true);
    this.auth.clearError();
    try {
      await this.auth.confirmSignUp(this.pendingEmail(), this.confirmRegisterForm.getRawValue().code);
      this.success.emit();
      this.visibleChange.emit(false);
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async onConfirmForgotSubmit(): Promise<void> {
    if (!this.confirmForgotForm.valid) return;
    const { newPassword, confirmPassword } = this.confirmForgotForm.getRawValue();
    if (newPassword !== confirmPassword) {
      this.confirmForgotForm.controls.confirmPassword.setErrors({ mismatch: true });
      return;
    }
    this.submitting.set(true);
    this.auth.clearError();
    try {
      await this.auth.confirmForgotPassword(
        this.pendingEmail(),
        this.confirmForgotForm.getRawValue().code,
        newPassword
      );
      this.success.emit();
      this.visibleChange.emit(false);
      this.viewChange.emit('login');
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected async resendCode(): Promise<void> {
    this.submitting.set(true);
    this.auth.clearError();
    try {
      await this.auth.resendSignUpCode(this.pendingEmail());
    } catch (err: unknown) {
      this.auth.setError(this.formatError(err));
    } finally {
      this.submitting.set(false);
    }
  }

  protected switchView(view: AuthView): void {
    this.auth.clearError();
    this.viewChange.emit(view);
  }

  protected togglePasswordVisibility(
    field: 'login' | 'register' | 'registerConfirm' | 'confirmForgot' | 'confirmForgotConfirm'
  ): void {
    if (field === 'login') this.showLoginPassword.update((v) => !v);
    else if (field === 'register') this.showRegisterPassword.update((v) => !v);
    else if (field === 'registerConfirm') this.showRegisterConfirmPassword.update((v) => !v);
    else if (field === 'confirmForgot') this.showConfirmForgotPassword.update((v) => !v);
    else this.showConfirmForgotConfirm.update((v) => !v);
  }

  private formatError(err: unknown): string {
    if (err && typeof err === 'object' && 'message' in err) {
      return String((err as { message: string }).message);
    }
    return 'An error occurred. Please try again.';
  }
}

